/* eslint-disable no-magic-numbers */
import React, { useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import get from 'lodash/get';
import { useNavigate, useParams } from 'react-router-dom';

import Icon from 'carbon-react/lib/components/icon';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { getReportingViewReportUrl, Logger } from '@futrli/shared-services';
import { toastError } from '@futrli/ui-components';

import { Data } from '../../../utils/table-filters/filter-table-types';
import { deleteScheduledReport, getScheduleReportDetails } from '../../../core/api/reporting/report-scheduling';
import { DeleteScheduleReportDialog } from './delete-schedule-report-dialog';
import { getHoursMinutes } from '../../../utils/dates';
import { NormalizedReportScheduleItem } from '../../../report-builder/components/document-library/scheduled-reports/scheduled-reports';
import { ScheduleReportDialog } from '../../../report-builder/components/schedule-report-dialog/schedule-report-dialog';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';

import * as styles from '../virtualized-list.scss';

interface Props {
  rowData?: Data;
  rowIndex: number;
  revalidateData?: () => Promise<void>;
}

export const ScheduleReportsTableRow = ({ rowData, rowIndex, revalidateData }: Props) => {
  const [isOpenScheduleReportDialog, setIsOpenScheduleReportDialog] = useState(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scheduledReportDetails, setScheduleReportDetails] = useState<NormalizedReportScheduleItem>();

  const navigate = useNavigate();

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { isConsolidation } = usePredictionsInfoContext();

  const handleOpenScheduleDialog = () => {
    setIsOpenScheduleReportDialog(true);
  };

  const handleEditClick = async () => {
    const scheduleReportId = get(rowData, 'id', '');
    if (scheduleReportId) {
      try {
        setIsLoading(true);
        handleOpenScheduleDialog();
        const fetchedScheduleReportDetails = await getScheduleReportDetails(
          workspaceId,
          organisationId,
          scheduleReportId,
          isConsolidation
        );
        setScheduleReportDetails({
          ...fetchedScheduleReportDetails,
          // TODO: what to use when  next_generate_at is null
          next_generate_at: fetchedScheduleReportDetails.next_generate_at
            ? new Date(fetchedScheduleReportDetails.next_generate_at)
            : fetchedScheduleReportDetails.end_date
            ? new Date(fetchedScheduleReportDetails.end_date)
            : new Date(fetchedScheduleReportDetails.start_date),
          users: fetchedScheduleReportDetails.users
            .map(u => u.email_address)
            .join(', ')
            .trim(),
          time: fetchedScheduleReportDetails.next_generate_at
            ? getHoursMinutes(fetchedScheduleReportDetails.next_generate_at)
            : fetchedScheduleReportDetails.end_date
            ? getHoursMinutes(fetchedScheduleReportDetails.end_date)
            : getHoursMinutes(fetchedScheduleReportDetails.start_date)
        });
      } catch (e) {
        Logger.error(e as string);
        toastError();
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCloseScheduleDialog = () => {
    setIsOpenScheduleReportDialog(false);
  };

  const handleOpenDeleteDialog = () => {
    setIsOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (rowData?.id) {
      try {
        setIsLoading(true);
        await deleteScheduledReport(workspaceId, organisationId, rowData.id, isConsolidation);
        revalidateData?.();
        handleCloseDeleteDialog();
      } catch (e) {
        Logger.error(e as string);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleReportNameClick = () => {
    navigate(getReportingViewReportUrl(workspaceId, organisationId, get(rowData, 'report', ''), isConsolidation));
  };

  if (!rowData) {
    return null;
  }

  return (
    <>
      <div
        role="row"
        key={rowData.id}
        className={clsx(styles.virtualizedListRow)}
        style={{ backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : 'transparent', position: 'relative' }}
      >
        <div
          role="cell"
          className={clsx(
            styles.virtualizedListCell,
            styles.flex3,
            styles.documentLibraryTableFont,
            styles.svgContainer
          )}
          style={{ minWidth: 0, border: 'none' }}
        >
          <div style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={handleReportNameClick}>
            <span className={styles.textOverflow}>{rowData.name}</span>
          </div>
        </div>
        {rowData.next_generate_at ? (
          <div
            role="cell"
            className={clsx(styles.virtualizedListCell, styles.flex1, styles.documentLibraryTableFont)}
            style={{ minWidth: 0, border: 'none' }}
          >
            <span className={styles.textOverflow}>{format(rowData.next_generate_at, 'dd/MM/yyyy')}</span>
          </div>
        ) : null}
        {rowData.time ? (
          <div
            role="cell"
            className={clsx(styles.virtualizedListCell, styles.flex1, styles.documentLibraryTableFont)}
            style={{ minWidth: 0, border: 'none' }}
          >
            <span className={styles.textOverflow}>{rowData.time}</span>
          </div>
        ) : null}
        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex2, styles.documentLibraryTableFont)}
          style={{ minWidth: 0, border: 'none' }}
        >
          <span>{rowData.period}</span>
        </div>

        <Tooltip message={rowData.users}>
          <div
            role="cell"
            className={clsx(styles.virtualizedListCell, styles.flex5, styles.documentLibraryTableFont)}
            style={{ minWidth: 0, border: 'none' }}
          >
            <div
              style={{
                width: 'calc(100% - 72px)',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis'
              }}
            >
              {rowData.users}
            </div>
          </div>
        </Tooltip>

        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex1, styles.documentLibraryTableFont)}
          style={{
            position: 'absolute',
            right: 0,
            display: 'flex',
            backgroundColor: '#f6f7fa',
            justifyContent: 'center',
            paddingRight: 0
          }}
        >
          <div className={styles.iconContainer} onClick={handleEditClick} style={{ marginRight: 12 }}>
            <Icon type="edit" />
          </div>
          <div className={styles.iconContainer} onClick={handleOpenDeleteDialog}>
            <Icon type="bin" />
          </div>
        </div>
      </div>

      {isOpenScheduleReportDialog && (
        <ScheduleReportDialog
          isOpen={isOpenScheduleReportDialog}
          handleClose={handleCloseScheduleDialog}
          reportName={rowData.pdf_name}
          reportId={get(rowData, 'report', '')}
          initialData={scheduledReportDetails}
          revalidateData={revalidateData}
          isLoading={isLoading}
        />
      )}

      {isOpenDeleteDialog && (
        <DeleteScheduleReportDialog
          isOpen={isOpenDeleteDialog}
          handleCloseDialog={handleCloseDeleteDialog}
          onSubmit={handleDelete}
          isLoading={isLoading}
        />
      )}
    </>
  );
};
